import React, { useCallback, useEffect, useMemo, useState } from "react";
import Icon from "@mdi/react";
import {
	mdiDoctor,
	mdiPlus,
	mdiChat,
	mdiDotsVertical,
	mdiFileDocumentEdit,
	mdiHeadCog,
	mdiCalendarClock,
	mdiClipboardPulse,
	mdiArrowLeft,
	mdiStethoscope,
	mdiAccountHeart,
	mdiEye,
	mdiMagnify,
	mdiChevronLeft,
	mdiContentSave,
	mdiNoteText,
} from "@mdi/js";
import {
	Button,
	CircularProgress,
	IconButton,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	MenuItem,
	Chip,
	Select,
	FormControl,
	InputLabel,
} from "@mui/material";
import { ListSidebar, MainLayout, useMobileLayout } from "layouts";
import { Chat } from "components";
import { useAccount } from "shared/hooks";
import { clsx } from "@ismael1361/utils";
import {
	NotistackServices,
	MedDoctorServices,
} from "shared/services";
import type {
	MedSpecialtyDTO,
	MedAgentDTO,
	MedMessageDTO,
	MedPatientDTO,
	MedAppointmentDTO,
	MedRecordDTO,
	MedAppointmentDetailDTO,
} from "shared/services/MedDoctorServices";
import styles from "./styles.module.scss";

// ── Types ─────────────────────────────────────────────────────────
type SidebarSection = "chat" | "patients" | "appointments" | "records" | "specialties";

// Ícones por tipo de agente
const AGENT_ICONS: Record<string, string> = {
	scribe: "📝",
	advisor: "🧠",
	nurse: "👩‍⚕️",
	triage: "🏥",
};

function getAgentEmoji(role: string): string {
	return AGENT_ICONS[role] || "🤖";
}

// Ícones por especialidade
const SPECIALTY_ICONS: Record<string, string> = {
	clinica_geral: "🩺", cardiologia: "❤️", dermatologia: "🧴", pediatria: "👶",
	ortopedia: "🦴", neurologia: "🧠", ginecologia: "🌸", oftalmologia: "👁️",
	psiquiatria: "🧠", urologia: "💧", endocrinologia: "🦋", pneumologia: "🫁",
};

function getSpecialtyEmoji(slug: string): string {
	return SPECIALTY_ICONS[slug] || "🏥";
}

// ── Main Component ────────────────────────────────────────────────

export const MedDoctorPage: React.FC = () => {
	const user = useAccount();
	const { isMobile, showContent, showSidebar, openDrawer } = useMobileLayout();

	// ── Data ──────────────────────────────────────────────────────
	const [specialties, setSpecialties] = useState<MedSpecialtyDTO[]>([]);
	const [agents, setAgents] = useState<MedAgentDTO[]>([]);
	const [patients, setPatients] = useState<MedPatientDTO[]>([]);
	const [appointments, setAppointments] = useState<MedAppointmentDTO[]>([]);
	const [loadingData, setLoadingData] = useState(false);

	// ── Navigation ────────────────────────────────────────────────
	const [activeSection, setActiveSection] = useState<SidebarSection>("chat");
	const [selectedSpecialtyId, setSelectedSpecialtyId] = useState<string | null>(null);

	// ── Chat ──────────────────────────────────────────────────────
	const [messages, setMessages] = useState<MedMessageDTO[]>([]);
	const [loadingMessages, setLoadingMessages] = useState(false);
	const [sendingMessage, setSendingMessage] = useState(false);

	// ── Add Patient Dialog ────────────────────────────────────────
	const [addPatientOpen, setAddPatientOpen] = useState(false);
	const [newPatientName, setNewPatientName] = useState("");
	const [newPatientEmail, setNewPatientEmail] = useState("");

	// ── Add Appointment Dialog ────────────────────────────────────
	const [addApptOpen, setAddApptOpen] = useState(false);
	const [apptPatientId, setApptPatientId] = useState("");
	const [apptSpecialtyId, setApptSpecialtyId] = useState("");
	const [apptDate, setApptDate] = useState("");
	const [apptNotes, setApptNotes] = useState("");

	// ── Save Chat to Appointment Dialog ───────────────────────────
	const [saveChatDialogOpen, setSaveChatDialogOpen] = useState(false);
	const [saveChatApptId, setSaveChatApptId] = useState("");

	// ── Records ───────────────────────────────────────────────────
	const [selectedPatientForRecords, setSelectedPatientForRecords] = useState<string | null>(null);
	const [records, setRecords] = useState<MedRecordDTO[]>([]);
	const [loadingRecords, setLoadingRecords] = useState(false);
	const [addRecordOpen, setAddRecordOpen] = useState(false);
	const [recordType, setRecordType] = useState("consultation");
	const [recordContent, setRecordContent] = useState("");
	const [recordPatientId, setRecordPatientId] = useState("");

	// ── Appointment Detail ────────────────────────────────────────
	const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
	const [appointmentDetail, setAppointmentDetail] = useState<MedAppointmentDetailDTO | null>(null);
	const [loadingDetail, setLoadingDetail] = useState(false);
	const [transcriptionText, setTranscriptionText] = useState("");
	const [savingTranscription, setSavingTranscription] = useState(false);

	// ── Active agent (derived) ────────────────────────────────────
	const activeAgent = useMemo(() => {
		if (!selectedSpecialtyId || agents.length === 0) return null;
		return agents.find(a => a.specialtyId === selectedSpecialtyId && a.agentType === "doctor") || agents[0];
	}, [agents, selectedSpecialtyId]);

	// ── Load Data ─────────────────────────────────────────────────
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				setLoadingData(true);
				const [specs, ags, pats, appts] = await Promise.all([
					MedDoctorServices.listSpecialties(),
					MedDoctorServices.listAgents(),
					MedDoctorServices.listPatients(),
					MedDoctorServices.listAppointments(),
				]);
				if (!cancelled) {
					setSpecialties(specs);
					setAgents(ags);
					setPatients(pats);
					setAppointments(appts);
					if (specs.length > 0 && !selectedSpecialtyId) {
						setSelectedSpecialtyId(specs[0].id);
					}
				}
			} catch (err: any) {
				NotistackServices.showErrorMessage(err?.message || "Erro ao carregar dados");
			} finally {
				if (!cancelled) setLoadingData(false);
			}
		})();
		return () => { cancelled = true; };
	}, []);

	// ── Load Conversation ─────────────────────────────────────────
	useEffect(() => {
		if (!activeAgent) return;
		let cancelled = false;
		(async () => {
			try {
				setLoadingMessages(true);
				const conv = await MedDoctorServices.getConversation(activeAgent.id);
				if (!cancelled) setMessages(conv.messages || []);
			} catch {
				if (!cancelled) setMessages([]);
			} finally {
				if (!cancelled) setLoadingMessages(false);
			}
		})();
		return () => { cancelled = true; };
	}, [activeAgent?.id]);

	// ── Send Message ──────────────────────────────────────────────
	const handleSendMessage = useCallback(async (content: string) => {
		if (!activeAgent || sendingMessage) return;
		try {
			setSendingMessage(true);
			const result = await MedDoctorServices.sendMessage(activeAgent.id, content);
			setMessages(prev => [...prev, result.userMessage, result.assistantMessage]);
		} catch (err: any) {
			NotistackServices.showErrorMessage(err?.message || "Erro ao enviar mensagem");
		} finally {
			setSendingMessage(false);
		}
	}, [activeAgent, sendingMessage]);

	// ── Handle Add Patient ────────────────────────────────────────
	const handleAddPatient = useCallback(async () => {
		if (!newPatientName.trim()) return;
		try {
			const patient = await MedDoctorServices.createPatient({
				full_name: newPatientName.trim(),
				email: newPatientEmail.trim() || undefined,
			});
			setPatients(prev => [...prev, patient]);
			setAddPatientOpen(false);
			setNewPatientName("");
			setNewPatientEmail("");
			NotistackServices.showSuccessMessage("Paciente adicionado com sucesso!");
		} catch (err: any) {
			NotistackServices.showErrorMessage(err?.message || "Erro ao adicionar paciente");
		}
	}, [newPatientName, newPatientEmail]);

	// ── Handle Add Appointment ────────────────────────────────────
	const handleAddAppointment = useCallback(async () => {
		if (!apptPatientId || !apptSpecialtyId || !apptDate) return;
		const patientName = patients.find(p => p.id === apptPatientId)?.fullName || "Consulta";
		const specName = specialties.find(s => s.id === apptSpecialtyId)?.name || "";
		try {
			const appt = await MedDoctorServices.createAppointment({
				patient_id: apptPatientId,
				title: `${specName} - ${patientName}`,
				scheduled_at: new Date(apptDate).toISOString(),
				specialty_id: apptSpecialtyId,
				description: apptNotes.trim() || undefined,
			});
			setAppointments(prev => [...prev, appt]);
			setAddApptOpen(false);
			setApptPatientId("");
			setApptSpecialtyId("");
			setApptDate("");
			setApptNotes("");
			NotistackServices.showSuccessMessage("Consulta agendada com sucesso!");
		} catch (err: any) {
			NotistackServices.showErrorMessage(err?.message || "Erro ao agendar consulta");
		}
	}, [apptPatientId, apptSpecialtyId, apptDate, apptNotes, patients, specialties]);

	// ── Load Records for Patient ──────────────────────────────────
	const loadRecords = useCallback(async (patientId: string) => {
		try {
			setLoadingRecords(true);
			setSelectedPatientForRecords(patientId);
			const allRecs = await MedDoctorServices.listRecords();
			setRecords(allRecs.filter(r => r.patientId === patientId));
		} catch (err: any) {
			NotistackServices.showErrorMessage(err?.message || "Erro ao carregar prontuários");
		} finally {
			setLoadingRecords(false);
		}
	}, []);

	// ── Handle Add Record ─────────────────────────────────────────
	const handleAddRecord = useCallback(async () => {
		if (!recordPatientId || !recordContent.trim()) return;
		try {
			const rec = await MedDoctorServices.createRecord({
				patient_id: recordPatientId,
				title: recordType === "consultation" ? "Consulta" : recordType === "prescription" ? "Receita" : recordType === "exam" ? "Exame" : recordType === "soap" ? "Nota SOAP" : "Registro",
				content: recordContent.trim(),
				record_type: recordType,
			});
			setRecords(prev => [...prev, rec]);
			setAddRecordOpen(false);
			setRecordContent("");
			setRecordType("consultation");
			NotistackServices.showSuccessMessage("Prontuário criado com sucesso!");
		} catch (err: any) {
			NotistackServices.showErrorMessage(err?.message || "Erro ao criar prontuário");
		}
	}, [recordPatientId, recordContent, recordType]);

	// ── Load Appointment Detail ───────────────────────────────────
	const loadAppointmentDetail = useCallback(async (appointmentId: string) => {
		try {
			setLoadingDetail(true);
			setSelectedAppointmentId(appointmentId);
			const detail = await MedDoctorServices.getAppointmentDetail(appointmentId);
			setAppointmentDetail(detail);
			setTranscriptionText("");
		} catch (err: any) {
			NotistackServices.showErrorMessage(err?.message || "Erro ao carregar detalhes da consulta");
		} finally {
			setLoadingDetail(false);
		}
	}, []);

	// ── Save Transcription ────────────────────────────────────────
	const handleSaveTranscription = useCallback(async () => {
		if (!selectedAppointmentId || !transcriptionText.trim()) return;
		try {
			setSavingTranscription(true);
			await MedDoctorServices.saveTranscription(selectedAppointmentId, transcriptionText.trim());
			NotistackServices.showSuccessMessage("Transcrição salva com sucesso!");
			// Recarregar detalhe para mostrar o record salvo
			await loadAppointmentDetail(selectedAppointmentId);
			setTranscriptionText("");
			// Atualizar lista de appointments (status pode ter mudado)
			const appts = await MedDoctorServices.listAppointments();
			setAppointments(appts);
		} catch (err: any) {
			NotistackServices.showErrorMessage(err?.message || "Erro ao salvar transcrição");
		} finally {
			setSavingTranscription(false);
		}
	}, [selectedAppointmentId, transcriptionText, loadAppointmentDetail]);

	// ── Save Chat as Transcription ────────────────────────────────
	const handleSaveChatAsTranscription = useCallback(async (appointmentId: string) => {
		if (messages.length === 0) return;
		const chatContent = messages.map(m => {
			const sender = m.role === "user" ? "Médico" : (activeAgent?.name || "IA");
			return `[${sender}]: ${m.content}`;
		}).join("\n\n");
		try {
			setSavingTranscription(true);
			await MedDoctorServices.saveTranscription(appointmentId, chatContent);
			NotistackServices.showSuccessMessage("Conversa salva como transcrição da consulta!");
			const appts = await MedDoctorServices.listAppointments();
			setAppointments(appts);
		} catch (err: any) {
			NotistackServices.showErrorMessage(err?.message || "Erro ao salvar transcrição");
		} finally {
			setSavingTranscription(false);
		}
	}, [messages, activeAgent]);

	// ── Loading State ─────────────────────────────────────────────
	if (loadingData) {
		return (
			<MainLayout>
				<div className={styles.emptyState}>
					<CircularProgress size={32} />
					<p>Carregando dados médicos…</p>
				</div>
			</MainLayout>
		);
	}

	// ── Chat Messages Formatting ──────────────────────────────────
	const chatMessages = messages.map(m => ({
		id: m.id,
		role: m.role as "user" | "assistant",
		content: m.content,
		createdAt: m.createdAt,
	}));

	// ── Sidebar ───────────────────────────────────────────────────
	const sidebar = (
		<ListSidebar>
			{/* Header */}
			<div className={styles.sidebarHeader}>
				<span className={styles.sidebarTitle}>
					{activeSection === "chat" && "Agentes IA"}
					{activeSection === "patients" && "Pacientes"}
					{activeSection === "appointments" && "Consultas"}
					{activeSection === "records" && "Prontuários"}
					{activeSection === "specialties" && "Especialidades"}
				</span>
				{activeSection === "patients" && (
					<IconButton size="small" className={styles.addBtn} onClick={() => setAddPatientOpen(true)}>
						<Icon path={mdiPlus} size={0.85} />
					</IconButton>
				)}
			</div>

			{/* Navigation Sections */}
			<div className={styles.sidebarSection}>
				<h3>Navegação</h3>
				{[
					{ key: "chat" as SidebarSection, icon: mdiChat, label: "Chat com IA" },
					{ key: "patients" as SidebarSection, icon: mdiAccountHeart, label: "Pacientes" },
					{ key: "appointments" as SidebarSection, icon: mdiCalendarClock, label: "Consultas" },
					{ key: "records" as SidebarSection, icon: mdiClipboardPulse, label: "Prontuários" },
					{ key: "specialties" as SidebarSection, icon: mdiStethoscope, label: "Especialidades" },
				].map(item => (
					<div
						key={item.key}
						className={clsx(styles.navItem, activeSection === item.key && styles.navItemActive)}
						onClick={() => setActiveSection(item.key)}
					>
						<Icon path={item.icon} size={0.85} />
						<span>{item.label}</span>
					</div>
				))}
			</div>

			{/* Agent/Data List */}
			{activeSection === "chat" && (
				<div className={styles.sidebarSection}>
					<h3>Agentes Médicos</h3>
					{loadingData ? (
						<div className={styles.emptySidebar}><CircularProgress size={20} /></div>
					) : agents.length === 0 ? (
						<div className={styles.emptySidebar}>
							<p>Nenhum agente disponível</p>
							<span className={styles.emptyHint}>Configure suas especialidades primeiro</span>
						</div>
					) : (
						agents.filter(a => a.agentType === "doctor").map(agent => (
							<div
								key={agent.id}
								className={clsx(styles.subjectCard, activeAgent?.id === agent.id && styles.subjectCardActive)}
								onClick={() => {
									setSelectedSpecialtyId(agent.specialtyId);
									if (isMobile) showContent();
								}}
							>
								<div className={styles.subjectIcon}>
									{getAgentEmoji(agent.role || "doctor")}
								</div>
								<div className={styles.subjectInfo}>
									<span className={styles.subjectName}>{agent.name}</span>
									<span className={styles.subjectDesc}>{agent.specialtyName || "Assistente IA"}</span>
								</div>
							</div>
						))
					)}
				</div>
			)}

			{activeSection === "patients" && (
				<div className={styles.sidebarSection}>
					<h3>Meus Pacientes</h3>
					{patients.length === 0 ? (
						<div className={styles.emptySidebar}>
							<p>Nenhum paciente cadastrado</p>
							<span className={styles.emptyHint}>Adicione pacientes usando o botão +</span>
						</div>
					) : (
						patients.map(patient => (
							<div key={patient.id} className={styles.subjectCard}>
								<div className={styles.subjectIcon}>👤</div>
								<div className={styles.subjectInfo}>
									<span className={styles.subjectName}>{patient.fullName}</span>
									<span className={styles.subjectDesc}>{patient.email || "Sem email"}</span>
								</div>
							</div>
						))
					)}
				</div>
			)}

			{activeSection === "specialties" && (
				<div className={styles.sidebarSection}>
					<h3>Especialidades</h3>
					{specialties.map(spec => (
						<div
							key={spec.id}
							className={clsx(styles.subjectCard, selectedSpecialtyId === spec.id && styles.subjectCardActive)}
							onClick={() => {
								setSelectedSpecialtyId(spec.id);
								setActiveSection("chat");
							}}
						>
							<div className={styles.subjectIcon}>{getSpecialtyEmoji(spec.slug)}</div>
							<div className={styles.subjectInfo}>
								<span className={styles.subjectName}>{spec.name}</span>
								<span className={styles.subjectDesc}>{spec.description || ""}</span>
							</div>
						</div>
					))}
				</div>
			)}
		</ListSidebar>
	);

	// ── Main Content ──────────────────────────────────────────────
	const renderMainContent = () => {
		// ── Appointments Section ──────────────────────────────────
		if (activeSection === "appointments") {
			// ── Appointment Detail View ──
			if (selectedAppointmentId) {
				if (loadingDetail) {
					return (<div className={styles.sectionContent}><div className={styles.emptySection}><CircularProgress size={32} /></div></div>);
				}
				const detail = appointmentDetail;
				const appt = detail?.appointment;
				const apptRecords = detail?.records || [];
				const transcriptions = apptRecords.filter((r: MedRecordDTO) => r.recordType === "transcription");
				const otherRecords = apptRecords.filter((r: MedRecordDTO) => r.recordType !== "transcription");
				const specName = specialties.find(s => s.id === appt?.specialtyId)?.name || "—";

				return (
					<div className={styles.sectionContent}>
						<div className={styles.sectionContentHeader}>
							<Button size="small" startIcon={<Icon path={mdiChevronLeft} size={0.7} />} onClick={() => { setSelectedAppointmentId(null); setAppointmentDetail(null); }}>
								Voltar
							</Button>
						</div>
						{appt && (
							<>
								<div className={styles.dataCard} style={{ marginBottom: "1.5rem" }}>
									<div className={styles.dataCardHeader}>
										<span className={styles.dataCardTitle}>📅 {appt.title || "Consulta"}</span>
										<Chip
											size="small"
											label={appt.status === "scheduled" ? "Agendada" : appt.status === "completed" ? "Concluída" : appt.status === "cancelled" ? "Cancelada" : appt.status}
											color={appt.status === "scheduled" ? "primary" : appt.status === "completed" ? "success" : "default"}
										/>
									</div>
									<div className={styles.dataCardBody}>
										<div className={styles.dataCardRow}><span className={styles.dataCardLabel}>Paciente</span><span>{detail?.patientName}</span></div>
										<div className={styles.dataCardRow}><span className={styles.dataCardLabel}>Especialidade</span><span>{specName}</span></div>
										<div className={styles.dataCardRow}><span className={styles.dataCardLabel}>Data</span><span>{new Date(appt.scheduledAt).toLocaleDateString("pt-BR")} às {new Date(appt.scheduledAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span></div>
										<div className={styles.dataCardRow}><span className={styles.dataCardLabel}>Duração</span><span>{appt.durationMinutes || 30} min</span></div>
										{appt.notes && <div className={styles.dataCardRow}><span className={styles.dataCardLabel}>Observações</span><span>{appt.notes}</span></div>}
									</div>
								</div>

								{/* Transcrições salvas */}
								{transcriptions.length > 0 && (
									<div style={{ marginBottom: "1.5rem" }}>
										<h3 style={{ fontSize: "1rem", marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
											<Icon path={mdiNoteText} size={0.8} /> Transcrições ({transcriptions.length})
										</h3>
										{transcriptions.map(rec => (
											<div key={rec.id} className={styles.dataCard} style={{ marginBottom: "0.75rem" }}>
												<div className={styles.dataCardHeader}>
													<span className={styles.dataCardTitle}>📝 {rec.title}</span>
													<span className={styles.dataCardDate}>{new Date(rec.createdAt).toLocaleDateString("pt-BR")} {new Date(rec.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>
												</div>
												<div className={styles.dataCardBody}>
													<p className={styles.dataCardText} style={{ whiteSpace: "pre-wrap" }}>{rec.content}</p>
												</div>
											</div>
										))}
									</div>
								)}

								{/* Outros registros */}
								{otherRecords.length > 0 && (
									<div style={{ marginBottom: "1.5rem" }}>
										<h3 style={{ fontSize: "1rem", marginBottom: "0.75rem" }}>📋 Outros Registros ({otherRecords.length})</h3>
										{otherRecords.map(rec => (
											<div key={rec.id} className={styles.dataCard} style={{ marginBottom: "0.75rem" }}>
												<div className={styles.dataCardHeader}>
													<span className={styles.dataCardTitle}>{rec.recordType === "clinical_analysis" ? "🧠" : rec.recordType === "consultation" ? "🩺" : rec.recordType === "prescription" ? "💊" : "📄"} {rec.title}</span>
													<span className={styles.dataCardDate}>{new Date(rec.createdAt).toLocaleDateString("pt-BR")}</span>
												</div>
												<div className={styles.dataCardBody}>
													<p className={styles.dataCardText} style={{ whiteSpace: "pre-wrap" }}>{rec.content}</p>
													{rec.structuredData && Object.keys(rec.structuredData).length > 0 && !rec.structuredData.parse_error && (
														<div style={{ marginTop: 8, padding: "8px 12px", background: "rgba(0,0,0,0.03)", borderRadius: 8 }}>
															{rec.structuredData.motivo_consulta && (
																<div className={styles.dataCardRow}>
																	<span className={styles.dataCardLabel}>Motivo</span>
																	<span>{rec.structuredData.motivo_consulta}</span>
																</div>
															)}
															{rec.structuredData.analise && (
																<div className={styles.dataCardRow}>
																	<span className={styles.dataCardLabel}>Análise</span>
																	<span>{rec.structuredData.analise}</span>
																</div>
															)}
															{rec.structuredData.plano?.length > 0 && (
																<div className={styles.dataCardRow}>
																	<span className={styles.dataCardLabel}>Plano</span>
																	<span>{rec.structuredData.plano.map((p: any) => p.descricao || p).join("; ")}</span>
																</div>
															)}
															{rec.structuredData.receita?.length > 0 && (
																<div className={styles.dataCardRow}>
																	<span className={styles.dataCardLabel}>Receita</span>
																	<span>{rec.structuredData.receita.map((r: any) => `${r.medicamento} — ${r.posologia || r.dose}`).join("; ")}</span>
																</div>
															)}
														</div>
													)}
												</div>
											</div>
										))}
									</div>
								)}

								{/* Adicionar nova transcrição */}
								<div className={styles.dataCard}>
									<div className={styles.dataCardHeader}>
										<span className={styles.dataCardTitle}>✍️ Adicionar Transcrição</span>
									</div>
									<div className={styles.dataCardBody}>
										<TextField
											fullWidth
											multiline
											rows={6}
											placeholder="Digite ou cole a transcrição da consulta aqui..."
											value={transcriptionText}
											onChange={e => setTranscriptionText(e.target.value)}
											variant="outlined"
											size="small"
										/>
										<div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", justifyContent: "flex-end" }}>
											<Button
												variant="contained"
												size="small"
												startIcon={<Icon path={mdiContentSave} size={0.7} />}
												onClick={handleSaveTranscription}
												disabled={!transcriptionText.trim() || savingTranscription}
											>
												{savingTranscription ? "Salvando…" : "Salvar Transcrição"}
											</Button>
										</div>
									</div>
								</div>
							</>
						)}
					</div>
				);
			}

			// ── Appointments List ──
			return (
				<div className={styles.sectionContent}>
					<div className={styles.sectionContentHeader}>
						<h2>📅 Consultas Agendadas</h2>
						<Button variant="contained" size="small" startIcon={<Icon path={mdiPlus} size={0.7} />} onClick={() => setAddApptOpen(true)}>
							Nova Consulta
						</Button>
					</div>
					{appointments.length === 0 ? (
						<div className={styles.emptySection}>
							<Icon path={mdiCalendarClock} size={3} color="var(--mui-palette-text-secondary)" style={{ opacity: 0.25 }} />
							<h3>Nenhuma consulta agendada</h3>
							<p>Agende uma nova consulta clicando no botão acima.</p>
						</div>
					) : (
						<div className={styles.dataGrid}>
							{appointments.sort((a, b) => new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime()).map(appt => {
								const patientName = patients.find(p => p.id === appt.patientId)?.fullName || "Paciente";
								const specName = specialties.find(s => s.id === appt.specialtyId)?.name || "—";
								return (
								<div key={appt.id} className={styles.dataCard} style={{ cursor: "pointer" }} onClick={() => loadAppointmentDetail(appt.id)}>
									<div className={styles.dataCardHeader}>
										<span className={styles.dataCardTitle}>{patientName}</span>
										<Chip
											size="small"
											label={appt.status === "scheduled" ? "Agendada" : appt.status === "completed" ? "Concluída" : appt.status === "cancelled" ? "Cancelada" : appt.status}
											color={appt.status === "scheduled" ? "primary" : appt.status === "completed" ? "success" : "default"}
										/>
									</div>
									<div className={styles.dataCardBody}>
										<div className={styles.dataCardRow}>
											<span className={styles.dataCardLabel}>Especialidade</span>
											<span>{specName}</span>
										</div>
										<div className={styles.dataCardRow}>
											<span className={styles.dataCardLabel}>Data</span>
											<span>{new Date(appt.scheduledAt).toLocaleDateString("pt-BR")} às {new Date(appt.scheduledAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>
										</div>
										<div className={styles.dataCardRow}>
											<span className={styles.dataCardLabel}>Duração</span>
											<span>{appt.durationMinutes || 30} min</span>
										</div>
										{appt.notes && (
											<div className={styles.dataCardRow}>
												<span className={styles.dataCardLabel}>Observações</span>
												<span>{appt.notes}</span>
											</div>
										)}
									</div>
									<div className={styles.dataCardActions}>
										<Button size="small" startIcon={<Icon path={mdiEye} size={0.6} />} onClick={(e) => { e.stopPropagation(); loadAppointmentDetail(appt.id); }}>
											Ver Detalhes
										</Button>
									</div>
								</div>
							);
							})}
						</div>
					)}
				</div>
			);
		}

		// ── Records Section ───────────────────────────────────────
		if (activeSection === "records") {
			return (
				<div className={styles.sectionContent}>
					<div className={styles.sectionContentHeader}>
						<h2>📋 Prontuários</h2>
						{selectedPatientForRecords && (
							<Button variant="contained" size="small" startIcon={<Icon path={mdiPlus} size={0.7} />} onClick={() => {
								setRecordPatientId(selectedPatientForRecords);
								setAddRecordOpen(true);
							}}>
								Novo Prontuário
							</Button>
						)}
					</div>
					{!selectedPatientForRecords ? (
						<div className={styles.emptySection}>
							<Icon path={mdiClipboardPulse} size={3} color="var(--mui-palette-text-secondary)" style={{ opacity: 0.25 }} />
							<h3>Selecione um paciente</h3>
							<p>Escolha um paciente abaixo para ver seus prontuários.</p>
							<div className={styles.patientPickerGrid}>
								{patients.map(p => (
									<div key={p.id} className={styles.patientPickerCard} onClick={() => loadRecords(p.id)}>
										<span className={styles.patientPickerAvatar}>👤</span>
										<span className={styles.patientPickerName}>{p.fullName}</span>
									</div>
								))}
								{patients.length === 0 && <p style={{ opacity: 0.5 }}>Nenhum paciente cadastrado</p>}
							</div>
						</div>
					) : loadingRecords ? (
						<div className={styles.emptySection}><CircularProgress size={32} /></div>
					) : (
						<>
							<Button size="small" startIcon={<Icon path={mdiChevronLeft} size={0.7} />} onClick={() => { setSelectedPatientForRecords(null); setRecords([]); }} sx={{ mb: 1 }}>
								Voltar para lista de pacientes
							</Button>
							<div className={styles.dataCardRow} style={{ marginBottom: "1rem" }}>
								<span className={styles.dataCardLabel}>Paciente:</span>
								<strong>{patients.find(p => p.id === selectedPatientForRecords)?.fullName}</strong>
							</div>
							{records.length === 0 ? (
								<div className={styles.emptySection}>
									<p>Nenhum prontuário encontrado para este paciente.</p>
								</div>
							) : (
								<div className={styles.dataGrid}>
									{records.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(rec => (
										<div key={rec.id} className={styles.dataCard}>
											<div className={styles.dataCardHeader}>
												<span className={styles.dataCardTitle}>
													{rec.recordType === "consultation" ? "🩺 Consulta" : rec.recordType === "clinical_analysis" ? "🧠 Prontuário Clínico" : rec.recordType === "prescription" ? "💊 Receita" : rec.recordType === "transcription" ? "🎙️ Transcrição" : rec.recordType === "exam" ? "🔬 Exame" : rec.recordType === "soap" ? "📝 SOAP" : `📄 ${rec.recordType || "Registro"}`}
												</span>
												<span className={styles.dataCardDate}>{new Date(rec.createdAt).toLocaleDateString("pt-BR")}</span>
											</div>
											<div className={styles.dataCardBody}>
												{rec.title && (
													<div className={styles.dataCardRow}>
														<span className={styles.dataCardLabel}>Título</span>
														<span>{rec.title}</span>
													</div>
												)}
												{rec.content && (
													<p className={styles.dataCardText} style={{ whiteSpace: "pre-wrap" }}>{rec.content}</p>
												)}
												{rec.structuredData && Object.keys(rec.structuredData).length > 0 && !rec.structuredData.parse_error && (
													<div style={{ marginTop: 8 }}>
														{rec.structuredData.motivo_consulta && (
															<div className={styles.dataCardRow}>
																<span className={styles.dataCardLabel}>Motivo</span>
																<span>{rec.structuredData.motivo_consulta}</span>
															</div>
														)}
														{rec.structuredData.analise && (
															<div className={styles.dataCardRow}>
																<span className={styles.dataCardLabel}>Análise</span>
																<span>{rec.structuredData.analise}</span>
															</div>
														)}
														{rec.structuredData.plano?.length > 0 && (
															<div className={styles.dataCardRow}>
																<span className={styles.dataCardLabel}>Plano</span>
																<span>{rec.structuredData.plano.map((p: any) => p.descricao || p).join("; ")}</span>
															</div>
														)}
														{rec.structuredData.receita?.length > 0 && (
															<div className={styles.dataCardRow}>
																<span className={styles.dataCardLabel}>Receita</span>
																<span>{rec.structuredData.receita.map((r: any) => `${r.medicamento} — ${r.posologia || r.dose}`).join("; ")}</span>
															</div>
														)}
													</div>
												)}
											</div>
										</div>
									))}
								</div>
							)}
						</>
					)}
				</div>
			);
		}

		// ── Patients Section (already has sidebar, show detail in main) ──
		if (activeSection === "patients") {
			return (
				<div className={styles.sectionContent}>
					<div className={styles.sectionContentHeader}>
						<h2>👥 Pacientes</h2>
						<Button variant="contained" size="small" startIcon={<Icon path={mdiPlus} size={0.7} />} onClick={() => setAddPatientOpen(true)}>
							Novo Paciente
						</Button>
					</div>
					{patients.length === 0 ? (
						<div className={styles.emptySection}>
							<Icon path={mdiAccountHeart} size={3} color="var(--mui-palette-text-secondary)" style={{ opacity: 0.25 }} />
							<h3>Nenhum paciente cadastrado</h3>
							<p>Adicione pacientes clicando no botão acima.</p>
						</div>
					) : (
						<div className={styles.dataGrid}>
							{patients.map(p => (
								<div key={p.id} className={styles.dataCard}>
									<div className={styles.dataCardHeader}>
										<span className={styles.dataCardTitle}>👤 {p.fullName}</span>
									</div>
									<div className={styles.dataCardBody}>
										{p.email && <div className={styles.dataCardRow}><span className={styles.dataCardLabel}>Email</span><span>{p.email}</span></div>}
										{p.phone && <div className={styles.dataCardRow}><span className={styles.dataCardLabel}>Telefone</span><span>{p.phone}</span></div>}
										{p.dateOfBirth && <div className={styles.dataCardRow}><span className={styles.dataCardLabel}>Nascimento</span><span>{new Date(p.dateOfBirth).toLocaleDateString("pt-BR")}</span></div>}
										{p.gender && <div className={styles.dataCardRow}><span className={styles.dataCardLabel}>Gênero</span><span>{p.gender}</span></div>}
										{p.cpf && <div className={styles.dataCardRow}><span className={styles.dataCardLabel}>CPF</span><span>{p.cpf}</span></div>}
										{p.insuranceProvider && <div className={styles.dataCardRow}><span className={styles.dataCardLabel}>Convênio</span><span>{p.insuranceProvider}</span></div>}
										{p.allergies?.length > 0 && <div className={styles.dataCardRow}><span className={styles.dataCardLabel}>Alergias</span><span>{p.allergies.join(", ")}</span></div>}
										{p.medications?.length > 0 && <div className={styles.dataCardRow}><span className={styles.dataCardLabel}>Medicamentos</span><span>{p.medications.join(", ")}</span></div>}
									</div>
									<div className={styles.dataCardActions}>
										<Button size="small" onClick={() => { setActiveSection("records"); loadRecords(p.id); }}>Ver Prontuários</Button>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			);
		}

		// ── Specialties Section ───────────────────────────────────
		if (activeSection === "specialties") {
			return (
				<div className={styles.sectionContent}>
					<div className={styles.sectionContentHeader}>
						<h2>🩺 Especialidades</h2>
						{specialties.length === 0 && (
							<Button variant="contained" size="small" onClick={async () => {
								try {
									const specs = await MedDoctorServices.initializeSpecialties();
									setSpecialties(specs);
									NotistackServices.showSuccessMessage("Especialidades inicializadas!");
								} catch (err: any) { NotistackServices.showErrorMessage(err?.message || "Erro"); }
							}}>
								Inicializar
							</Button>
						)}
					</div>
					{specialties.length === 0 ? (
						<div className={styles.emptySection}>
							<Icon path={mdiStethoscope} size={3} color="var(--mui-palette-text-secondary)" style={{ opacity: 0.25 }} />
							<h3>Nenhuma especialidade configurada</h3>
							<p>Clique em "Inicializar" para criar as especialidades padrão.</p>
						</div>
					) : (
						<div className={styles.dataGrid}>
							{specialties.map(spec => (
								<div key={spec.id} className={styles.dataCard} onClick={() => { setSelectedSpecialtyId(spec.id); setActiveSection("chat"); }}>
									<div className={styles.dataCardHeader}>
										<span className={styles.dataCardTitle}>{getSpecialtyEmoji(spec.slug)} {spec.name}</span>
										<Chip size="small" label={spec.isActive ? "Ativa" : "Inativa"} color={spec.isActive ? "success" : "default"} />
									</div>
									{spec.description && <div className={styles.dataCardBody}><p className={styles.dataCardText}>{spec.description}</p></div>}
								</div>
							))}
						</div>
					)}
				</div>
			);
		}

		// ── Default: Chat Section ─────────────────────────────────
		return (
			<>
				<div className={styles.chatHeader}>
					{isMobile && (
						<button className={styles.mobileBackBtn} onClick={showSidebar}>
							<Icon path={mdiArrowLeft} size={0.9} />
						</button>
					)}
					<div className={styles.chatHeaderAvatar}>
						<span>{activeAgent ? getAgentEmoji(activeAgent.role || "doctor") : "🤖"}</span>
					</div>
					<div className={styles.chatHeaderInfo}>
						<span className={styles.chatHeaderName}>{activeAgent?.name || "Selecione um agente"}</span>
						<span className={styles.chatHeaderDesc}>{activeAgent?.specialtyName || "Assistente médico IA"}</span>
					</div>
					<div className={styles.chatHeaderActions}>
						{messages.length > 0 && appointments.length > 0 && (
							<Button
								size="small"
								variant="outlined"
								startIcon={<Icon path={mdiContentSave} size={0.6} />}
								onClick={() => setSaveChatDialogOpen(true)}
								sx={{ mr: 0.5, fontSize: "0.75rem", textTransform: "none" }}
							>
								Salvar em Consulta
							</Button>
						)}
						{isMobile && (
							<IconButton size="small" className={styles.chatHeaderMenuBtn} onClick={openDrawer}>
								<Icon path={mdiDotsVertical} size={0.9} />
							</IconButton>
						)}
					</div>
				</div>
				{activeAgent ? (
					<Chat onSend={handleSendMessage} placeholder="Descreva o caso clínico ou tire sua dúvida..." disabled={sendingMessage}>
						{loadingMessages && (
							<div className={styles.emptyChat}>
								<CircularProgress color="inherit" size={24} />
								<span>Carregando conversa...</span>
							</div>
						)}
						{!loadingMessages && messages.length === 0 && (
							<div className={styles.emptyChat}>
								<Icon path={mdiStethoscope} size={2.5} style={{ opacity: 0.3 }} />
								<h3>{activeAgent.name}</h3>
								<p>Descreva o caso clínico ou tire sua dúvida médica.</p>
							</div>
						)}
						{messages.map((msg) => {
							const outgoing = msg.role === "user";
							return (
								<Chat.Message key={msg.id} id={msg.id} incoming={!outgoing} outgoing={outgoing} senderName={outgoing ? (user?.firstName || "Você") : activeAgent.name}>
									{msg.content}
								</Chat.Message>
							);
						})}
					</Chat>
				) : (
					<div className={styles.emptyChat}>
						<Icon path={mdiStethoscope} size={3} color="var(--mui-palette-text-secondary)" style={{ opacity: 0.3 }} />
						<h3>Selecione um agente IA</h3>
						<p>Escolha um agente médico na barra lateral para iniciar uma consulta assistida por IA.</p>
					</div>
				)}
			</>
		);
	};

	const mainContent = renderMainContent();

	return (
		<MainLayout sidebar={sidebar}>
			{mainContent}

			{/* Add Patient Dialog */}
			<Dialog open={addPatientOpen} onClose={() => setAddPatientOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>Novo Paciente</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						label="Nome Completo"
						fullWidth
						margin="dense"
						value={newPatientName}
						onChange={e => setNewPatientName(e.target.value)}
					/>
					<TextField
						label="Email (opcional)"
						fullWidth
						margin="dense"
						value={newPatientEmail}
						onChange={e => setNewPatientEmail(e.target.value)}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setAddPatientOpen(false)}>Cancelar</Button>
					<Button variant="contained" onClick={handleAddPatient} disabled={!newPatientName.trim()}>
						Adicionar
					</Button>
				</DialogActions>
			</Dialog>

			{/* Add Appointment Dialog */}
			<Dialog open={addApptOpen} onClose={() => setAddApptOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Nova Consulta</DialogTitle>
				<DialogContent>
					<FormControl fullWidth margin="dense">
						<InputLabel>Paciente</InputLabel>
						<Select value={apptPatientId} label="Paciente" onChange={e => setApptPatientId(e.target.value)}>
							{patients.map(p => <MenuItem key={p.id} value={p.id}>{p.fullName}</MenuItem>)}
						</Select>
					</FormControl>
					<FormControl fullWidth margin="dense">
						<InputLabel>Especialidade</InputLabel>
						<Select value={apptSpecialtyId} label="Especialidade" onChange={e => setApptSpecialtyId(e.target.value)}>
							{specialties.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
						</Select>
					</FormControl>
					<TextField
						label="Data e Hora"
						type="datetime-local"
						fullWidth
						margin="dense"
						value={apptDate}
						onChange={e => setApptDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
					/>
					<TextField
						label="Observações (opcional)"
						fullWidth
						margin="dense"
						multiline
						rows={3}
						value={apptNotes}
						onChange={e => setApptNotes(e.target.value)}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setAddApptOpen(false)}>Cancelar</Button>
					<Button variant="contained" onClick={handleAddAppointment} disabled={!apptPatientId || !apptSpecialtyId || !apptDate}>
						Agendar
					</Button>
				</DialogActions>
			</Dialog>

			{/* Add Record Dialog */}
			<Dialog open={addRecordOpen} onClose={() => setAddRecordOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Novo Prontuário</DialogTitle>
				<DialogContent>
					<FormControl fullWidth margin="dense">
						<InputLabel>Tipo</InputLabel>
						<Select value={recordType} label="Tipo" onChange={e => setRecordType(e.target.value)}>
							<MenuItem value="consultation">🩺 Consulta</MenuItem>
							<MenuItem value="prescription">💊 Receita</MenuItem>
							<MenuItem value="exam">🔬 Exame</MenuItem>
							<MenuItem value="soap">📝 Nota SOAP</MenuItem>
							<MenuItem value="other">📄 Outro</MenuItem>
						</Select>
					</FormControl>
					<TextField
						label="Conteúdo"
						fullWidth
						margin="dense"
						multiline
						rows={6}
						value={recordContent}
						onChange={e => setRecordContent(e.target.value)}
						placeholder="Descreva os detalhes do prontuário..."
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setAddRecordOpen(false)}>Cancelar</Button>
					<Button variant="contained" onClick={handleAddRecord} disabled={!recordContent.trim()}>
						Salvar
					</Button>
				</DialogActions>
			</Dialog>

			{/* Save Chat as Transcription Dialog */}
			<Dialog open={saveChatDialogOpen} onClose={() => setSaveChatDialogOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>Salvar Conversa como Transcrição</DialogTitle>
				<DialogContent>
					<p style={{ marginBottom: "1rem", color: "var(--mui-palette-text-secondary)", fontSize: "0.875rem" }}>
						Selecione a consulta à qual deseja vincular esta conversa como transcrição.
					</p>
					<FormControl fullWidth margin="dense">
						<InputLabel>Consulta</InputLabel>
						<Select value={saveChatApptId} label="Consulta" onChange={e => setSaveChatApptId(e.target.value)}>
							{appointments.map(a => {
								const pName = patients.find(p => p.id === a.patientId)?.fullName || "Paciente";
								return <MenuItem key={a.id} value={a.id}>{pName} — {new Date(a.scheduledAt).toLocaleDateString("pt-BR")} ({a.title})</MenuItem>;
							})}
						</Select>
					</FormControl>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setSaveChatDialogOpen(false)}>Cancelar</Button>
					<Button variant="contained" disabled={!saveChatApptId || savingTranscription} onClick={async () => {
						await handleSaveChatAsTranscription(saveChatApptId);
						setSaveChatDialogOpen(false);
						setSaveChatApptId("");
					}}>
						{savingTranscription ? "Salvando…" : "Salvar"}
					</Button>
				</DialogActions>
			</Dialog>
		</MainLayout>
	);
};
